import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import WaveSurfer from "wavesurfer.js";
import '../App.scss'

const AudioPlayer = forwardRef(({ audioUrl, onPlayStateChange }, ref) => {
  const waveformRef = useRef(null);
  const mediaElRef = useRef(null);
  const wavesurfer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);

  useImperativeHandle(ref, () => ({
    play() {
      safePlay();
    },
    pause() {
      if (wavesurfer.current) wavesurfer.current.pause();
    },
    stop() {
      if (wavesurfer.current) wavesurfer.current.stop();
    },
  }));

  // Must be called synchronously from a user gesture on iOS.
  const safePlay = () => {
    const el = mediaElRef.current;
    if (!el) return;
    const p = el.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => setError(true));
    }
  };

  useEffect(() => {
    setError(false);
    setDuration(0);

    const timeout = setTimeout(() => {
      if (!waveformRef.current || !mediaElRef.current) return;

      wavesurfer.current = WaveSurfer.create({
        container: waveformRef.current,
        // Drive playback through our own <audio> element so iOS Safari
        // unlocks it on the user's tap and native codecs are used.
        media: mediaElRef.current,
        waveColor: "#ccc",
        progressColor: "#000000",
        cursorColor: "blue",
        height: 50,
        barWidth: 2,
        barRadius: 2,
      });

      wavesurfer.current.on("ready", () => {
        setDuration(wavesurfer.current.getDuration());
      });
      wavesurfer.current.on("play", () => {
        setPlaying(true);
        onPlayStateChange && onPlayStateChange(true);
      });
      wavesurfer.current.on("pause", () => {
        setPlaying(false);
        onPlayStateChange && onPlayStateChange(false);
      });
      wavesurfer.current.on("finish", () => {
        setPlaying(false);
        onPlayStateChange && onPlayStateChange(false);
      });
      wavesurfer.current.on("error", () => setError(true));
    }, 150);

    return () => {
      clearTimeout(timeout);
      if (wavesurfer.current) {
        wavesurfer.current.destroy();
        wavesurfer.current = null;
      }
    };
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (playing) {
      if (wavesurfer.current) wavesurfer.current.pause();
    } else {
      safePlay();
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="voice-player" style={{ position: "relative" }}>
      <audio ref={mediaElRef} src={audioUrl} preload="metadata" playsInline />

      <button onClick={handlePlayPause}>
        {playing ? "⏸" : "▶"}
      </button>

      <div id="waveform" ref={waveformRef}></div>

      <div
        style={{
          padding: "2px 4px",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      >
        {error ? "Playback not supported" : `${formatTime(duration)} `}
      </div>
    </div>
  );
});

export default AudioPlayer;
